import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "auto-repair-web-design-colorado",
  metaTitle: "Auto Repair Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom auto repair web design across Colorado — fast, mobile-first, conversion-tested sites built to convert local searches into booked jobs. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Auto repair marketing", to: "/auto-repair-marketing-colorado" },
  ],
  currentLabel: "Auto Repair Web Design Colorado",
  eyebrow: "Auto Repair Web Design — Colorado",
  h1: (
    <>
      Auto Repair <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded auto repair websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Auto Repair websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first auto repair sites with sticky click-to-call, dedicated service pages, and conversion-tested booking flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Auto Repair web design in Colorado",
    body: "Auto Repair web design is the practice of building conversion-focused, mobile-first websites for auto repair companies — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second load — \"auto repair near me\" is a mobile-first emergency search." },
    { title: "Service pages per repair type", body: "Brakes, transmission, AC, diagnostics, oil change, alignment — each ranks for its own queries." },
    { title: "Online appointment booking", body: "Integration with Shopmonkey, Tekmetric, or Mitchell1 for real-time booking." },
    { title: "Sticky click-to-call", body: "Phone visible on every scroll — most shop calls happen from a phone in a parking lot." },
    { title: "ASE certification badges", body: "Certifications, warranties, and labor guarantees surfaced above the fold." },
    { title: "Schema and SEO baked in", body: "AutoRepair, Service, FAQPage, Review schema at launch." },
  ],
  whyThisMarket: [
    { heading: "Emergency intent dominates", body: "Half of auto repair searches happen with the car already broken down. Speed wins or loses the call." },
    { heading: "Specialty pages outrank generalists", body: "European, diesel, transmission, and EV specialty shops rank circles around generalists with proper pages." },
    { heading: "Reviews are the entire close", body: "We surface Google reviews and warranty terms above the fold — the two things drivers actually decide on." },
  ],
  relatedLinks: [
    { to: "/auto-repair-marketing-colorado", label: "Auto repair marketing in Colorado — full hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado auto repair case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a auto repair website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a auto repair website?", a: "Typical timeline is 4–6 weeks for a 12–20 page auto repair site with full service and service-area pages. Larger multi-location auto repair companies can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs auto repair companies?", a: "No — we build auto repair sites for companies across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Auto Repair Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for auto repair companies across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/auto-repair-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
